/**
 * 💬 업데이트 문구 고르기 — 쓸 말이 생각나지 않을 때 대분류 → 문구 (v2.216.0 · 2026-09-29)
 *
 * 사용자 요청: 「딜 업데이트 추가로 입력할때 쓸말 생각안날때 대분류로 abcde중 고르고 안에 맨트 골라서 활용할수 있게」
 *
 * 어디에 — 상세 창 「📝 업데이트 이력」의 넣기 칸 · [수정] 창의 「📝 새 업데이트」 칸 (두 곳이 이 부품 하나를 쓴다).
 * 어떻게 — 문구를 고르면 **칸에 글자로 들어간다**(저장은 사람이 한다 · 넣고 나서 고쳐도 된다).
 *   · 칸이 비어 있으면: 「Request for extend. / 한글 / 영문」 (☑ 앞에 붙임이 켜져 있을 때 · 운영 자료의 연장 경과 줄 모양)
 *   · 이미 글이 있으면: 그 아래 줄에 문구만 (두 문구를 이어 붙일 때 — 머리는 첫 줄에 이미 있다)
 * 목록은 서버가 대분류로 나눠 준다(app_config · 기본 50문구) — 나누는 규칙을 화면에 베끼지 않는다.
 * ☑ 앞에 붙임은 브라우저에 기억한다(localStorage · 못 읽어도 켜진 것으로).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as api from '../lib/deal-registration-api';
import { DealUpdatePhrasesModal, UPDATE_PHRASES_QUERY_KEY } from './DealUpdatePhrasesModal';

const EXTEND_LS_KEY = 'deal.updatePhrase.extend';

function readExtendPref(): boolean {
  try {
    const v = localStorage.getItem(EXTEND_LS_KEY);
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
}

/**
 * 고른 문구를 칸의 글에 붙인다(순수).
 *   빈 칸 → 머리(있으면) + 문구 · 글이 있으면 → 끝 공백을 걷고 새 줄에 문구만.
 */
export function insertPhrase(current: string, phrase: string, prefix: string | null): string {
  const cur = (current ?? '').replace(/\s+$/, '');
  if (!cur) return prefix ? `${prefix} / ${phrase}` : phrase;
  return `${cur}\n${phrase}`;
}

export function DealUpdatePhrasePicker({
  value,
  onChange,
}: {
  /** 지금 칸에 적힌 글 */
  value: string;
  /** 문구를 붙인 새 글 */
  onChange: (next: string) => void;
}): React.JSX.Element {
  const q = useQuery({ queryKey: UPDATE_PHRASES_QUERY_KEY, queryFn: api.getUpdatePhrases, staleTime: 5 * 60 * 1000 });
  const groups = q.data?.groups ?? [];
  const [groupKey, setGroupKey] = useState('');
  const [extend, setExtend] = useState<boolean>(readExtendPref);
  const [listOpen, setListOpen] = useState(false);
  const group = groups.find((g) => g.key === groupKey) ?? groups[0];

  const setExtendPref = (v: boolean): void => {
    setExtend(v);
    try { localStorage.setItem(EXTEND_LS_KEY, v ? '1' : '0'); } catch { /* 기억 못 해도 고르기는 된다 */ }
  };

  const pick = (idx: string): void => {
    if (!group || idx === '') return;
    const p = group.phrases[Number(idx)];
    if (!p) return;
    onChange(insertPhrase(value, p.text, extend ? (q.data?.extendPrefix ?? null) : null));
  };

  const prefix = q.data?.extendPrefix ?? 'Request for extend.';

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]" data-testid="deal-phrase-picker">
      <span className="text-slate-500 whitespace-nowrap">💬 문구 고르기</span>
      <select
        aria-label="대분류"
        value={group?.key ?? ''}
        disabled={groups.length === 0}
        onChange={(e) => setGroupKey(e.target.value)}
        className="ui-input ui-input-sm max-w-[12rem]"
      >
        {groups.length === 0 ? <option value="">{q.isLoading ? '불러오는 중…' : q.isError ? '목록 없음' : '문구 없음'}</option> : null}
        {groups.map((g) => <option key={g.key} value={g.key}>{g.key}. {g.title}</option>)}
      </select>
      <select
        aria-label="문구"
        value=""
        disabled={!group}
        onChange={(e) => pick(e.target.value)}
        // 문구가 길다 — 최소 20rem 을 잡아, 좁은 상세 창(768px)에서는 ☑·✎ 가 아랫줄로 내려가고 이 칸이 첫 줄을 다 쓴다(실측 236px → 500px대)
        className="ui-input ui-input-sm flex-1 min-w-[20rem] max-w-[40rem]"
      >
        <option value="">
          {group ? `— 고르면 칸에 들어갑니다 (${group.phrases.length}개 · ● 범용형) —` : q.isError ? '문구 목록을 불러오지 못했습니다' : '—'}
        </option>
        {group?.phrases.map((p, i) => (
          <option key={i} value={String(i)}>{p.generic ? '● ' : ''}{p.text}</option>
        ))}
      </select>
      <label
        className="inline-flex items-center gap-1 whitespace-nowrap text-slate-600 cursor-pointer"
        title="연장 요청 줄 — 운영 자료의 「Request for extend. / 한글 / 영문」 모양. 칸이 비어 있을 때만 앞에 붙습니다."
      >
        <input type="checkbox" checked={extend} onChange={(e) => setExtendPref(e.target.checked)} />
        「{prefix}」 앞에 붙임
      </label>
      <button
        type="button"
        onClick={() => setListOpen(true)}
        title="문구 목록 보기 · 고치기(경영지원·관리자)"
        className="text-slate-500 hover:text-blue-600 whitespace-nowrap"
      >
        ✎ 목록
      </button>
      {listOpen ? <DealUpdatePhrasesModal onClose={() => setListOpen(false)} /> : null}
    </div>
  );
}
